import { useCallback, useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import MonthDropdown from "../../../../components/inputs/MonthDropdown";
import YearDropdown from "../../../../components/inputs/YearDropdown";
import { DropdownOption } from "../../../../components/inputs/Dropdown";

import { MONTHS_OPTIONS } from "../../../../components/inputs/consts";

import { getBillsByDate, getEmpBills, finalizeDocument } from "../../../../services/clearing/bills";
import useToastActionWrapper from "../../../../hooks/useToastActionWrapper";

interface EmpBillsGenerationToolbarProps {
  mandateName: string;
  invoiceEmail?: string;
}

const today = new Date();

const EmpBillsGenerationToolbar = ({
  mandateName,
  invoiceEmail,
}: EmpBillsGenerationToolbarProps) => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    today.getMonth()
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(
    today.getFullYear()
  );

  const [currentBills, setCurrentBills] = useState([]);
  const [billsError, setBillsError] = useState<string | null>(null);
  const [billsLoading, setBillsLoading] = useState(false);

  const fetchCurrentBills = useCallback(async () => {
    if (!mandateName) return;
    setBillsLoading(true);
    setBillsError(null);
    try {
      const data = await getEmpBills({ mandateName });
      setCurrentBills(data || []);
    } catch (e: any) {
      const message =
        e?.response?.data?.message || e?.response?.data || e?.message || "Failed to fetch current bills";
      setBillsError(String(message));
      setCurrentBills([]);
    } finally {
      setBillsLoading(false);
    }
  }, [mandateName]);

  useEffect(() => {
    fetchCurrentBills();
  }, [fetchCurrentBills]);

  const onFinalizeAndSend = useCallback((documentId: number) => {
    confirmDialog({
      header: "Finalize and Send",
      message: (
        <div>
          <p>You are about to finalize and send document <strong>{documentId}</strong> to:</p>
          <p><strong>{invoiceEmail || "unknown email"}</strong></p>
          <p style={{ color: "red", fontWeight: "bold" }}>THIS ACTION CAN NOT BE REVERSED</p>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Send and Finalize",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await finalizeDocument(documentId);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Document ${documentId} finalized and sent.`,
            life: 3000,
          });
          fetchCurrentBills();
        } catch (e: any) {
          const message =
            e?.response?.data?.message || e?.response?.data || e?.message || "Failed to finalize document";
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: String(message),
            life: 5000,
          });
        }
      },
    });
  }, [invoiceEmail, fetchCurrentBills]);

  const toast = useRef<Toast>(null);
  const { toastActionWrapper } = useToastActionWrapper(toast);

  const onGenerateBills = useCallback(() => {
    toastActionWrapper({
      action: () =>
        getBillsByDate({
          mandateName,
          year: selectedYear,
          month: selectedMonth,
        }),
      successMessage: `Successfully generated EMP bills for ${
        MONTHS_OPTIONS.find((m) => m.code === selectedMonth).name
      } ${selectedYear}!`,
      errorMessage: "Failed to generate EMP bills!",
    });
  }, [selectedMonth, selectedYear, mandateName, toastActionWrapper]);

  return (
    <section className="py-4">
      <ConfirmDialog />
      <Toast ref={toast} />
      <Card
        title={"Generate Bills for EMP: " + mandateName}
        id="emp-bills-generation"
      >
        <form className="grid align-items-center">
          <div className="col-12 md:col-4">
            <MonthDropdown
              monthIndex={selectedMonth}
              onChange={(opt) => setSelectedMonth(Number(opt.code))}
              placeholder="Select Month"
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-4">
            <YearDropdown
              year={selectedYear}
              onChange={(opt: DropdownOption | null) =>
                setSelectedYear(Number(opt.code))
              }
              placeholder="Select Year"
              className="w-full"
              yearsOffset={2}
            />
          </div>

          <div className="col-12 md:col-4 flex flex-column gap-2 justify-content-end">
            <Button
              label="Generate EMP Bills"
              onClick={(e) => {
                e.preventDefault();
                onGenerateBills();
              }}
              className="w-full"
            />
          </div>
        </form>
      </Card>

      <Card title="Current Bills" className="mt-3">
        <div className="flex justify-content-end mb-3">
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={() => fetchCurrentBills()}
            className="p-button-outlined"
            loading={billsLoading}
          />
        </div>
        {billsLoading && (
          <div className="flex justify-content-center my-3">
            <ProgressSpinner />
          </div>
        )}
        {billsError && (
          <Message severity="error" text={billsError} className="w-full mb-3" />
        )}
        {!billsLoading && !billsError && (
          <DataTable value={currentBills} sortField="date" sortOrder={-1} emptyMessage="No bills found for the selected period.">
            <Column field="date" header="Date" sortable />
            <Column field="documentId" header="Document ID" />
            <Column field="invoiceEmail" header="Invoice Email" />
            <Column field="dueInDays" header="Due in Days" />
            <Column field="vatOption" header="VAT Option" />
            <Column
              field="isDraft"
              header="Draft"
              body={(row) => (row.isDraft ? "Yes" : "No")}
            />
            <Column
              field="easybillUrl"
              header="Easybill"
              body={(row) =>
                row.easybillUrl ? (
                  <Button
                    label="Show in Easybill"
                    icon="pi pi-external-link"
                    className="p-button-sm p-button-outlined"
                    onClick={() => window.open(row.easybillUrl, "_blank", "noopener,noreferrer")}
                  />
                ) : (
                  "-"
                )
              }
            />
            <Column
              header="Actions"
              body={(row) =>
                row.isDraft ? (
                  <Button
                    label="Finalize & Send"
                    icon="pi pi-send"
                    className="p-button-warning p-button-sm"
                    onClick={() => onFinalizeAndSend(row.documentId)}
                  />
                ) : null
              }
            />
          </DataTable>
        )}
      </Card>
    </section>
  );
};

export default EmpBillsGenerationToolbar;
